import React, { useState } from 'react';
import { Check, Clock3, ExternalLink, MapPin, UserCheck, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUEST_LABELS = {
  leave: 'Leave',
  time_correction: 'Time correction / change',
  permission: 'Permission',
  camp_duty: 'Camp duty',
};

const formatIstTime = (value) => new Date(value).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' });
const formatDuration = (minutes) => `${Math.floor((minutes || 0) / 60)}h ${(minutes || 0) % 60}m`;

export const AttendanceAdminOverview = ({ data, onUpdated }) => {
  const [reviewingId, setReviewingId] = useState('');
  const requests = data?.requests || [];
  const staffSummary = data?.staff_summary || [];

  const reviewRequest = async (requestId, status) => {
    setReviewingId(requestId);
    try {
      await axios.put(
        `${API}/staff/attendance/requests/${requestId}`,
        { status, review_notes: '' },
        { withCredentials: true },
      );
      toast.success(`Attendance request ${status}`);
      await onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to review request');
    }
    setReviewingId('');
  };

  return (
    <section className="bg-white rounded-xl p-5 shadow-sm" data-testid="dashboard-attendance-overview">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-semibold text-[#0A2540]">Attendance & Requests</h3>
          <p className="mt-1 text-sm text-[#64748B]">Today’s GPS attendance and requests awaiting approval</p>
        </div>
        <div className="flex flex-wrap gap-2 text-xs font-semibold">
          <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-emerald-700" data-testid="dashboard-attendance-present-count">{data?.staff_present || 0} present</span>
          <span className="rounded-full bg-sky-50 px-3 py-1.5 text-sky-700" data-testid="dashboard-attendance-open-count">{data?.open_sessions || 0} checked in</span>
          <span className="rounded-full bg-cyan-50 px-3 py-1.5 text-cyan-700" data-testid="dashboard-attendance-total-duration">{formatDuration(data?.total_duration_minutes)} worked</span>
          <span className="rounded-full bg-amber-50 px-3 py-1.5 text-amber-700" data-testid="dashboard-attendance-pending-count">{data?.pending_requests || 0} pending</span>
        </div>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <div>
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#0A2540]"><Clock3 className="h-4 w-4 text-sky-600" /> Staff attendance today</div>
          <div className="space-y-2" data-testid="dashboard-attendance-staff-list">
            {staffSummary.length === 0 ? <p className="text-sm text-[#94A3B8]">No attendance captured today.</p> : staffSummary.map((staff) => (
              <div key={staff.staff_id} className="rounded-lg border border-[#E2E8F0] px-3 py-2.5" data-testid={`dashboard-attendance-staff-${staff.staff_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0"><p className="truncate text-sm font-semibold text-[#0A2540]">{staff.staff_name}</p><p className="text-xs capitalize text-[#64748B]">{staff.role?.replaceAll('_', ' ')} · {staff.session_count} session{staff.session_count === 1 ? '' : 's'}</p></div>
                  <span className="text-sm font-bold text-emerald-700" data-testid={`dashboard-attendance-duration-${staff.staff_id}`}>{formatDuration(staff.total_duration_minutes)}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#64748B]">
                  <span>In {formatIstTime(staff.first_check_in_at)}</span>
                  <span>Out {staff.last_check_out_at ? formatIstTime(staff.last_check_out_at) : '—'}</span>
                  {staff.has_open_session && <span className="font-semibold text-sky-700">Checked in</span>}
                  <a href={staff.latest_map_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-sky-700 hover:text-sky-900" data-testid={`dashboard-attendance-map-${staff.staff_id}`}><MapPin className="h-3.5 w-3.5" /> GPS <ExternalLink className="h-3 w-3" /></a>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2"><div className="flex items-center gap-2 text-sm font-semibold text-[#0A2540]"><UserCheck className="h-4 w-4 text-amber-600" /> Pending requests</div>{(data?.pending_requests || 0) > requests.length && <span className="text-xs text-[#64748B]" data-testid="dashboard-attendance-request-limit-note">Showing latest {requests.length} of {data.pending_requests}</span>}</div>
          <div className="space-y-2" data-testid="dashboard-attendance-request-list">
            {requests.length === 0 ? <p className="text-sm text-[#94A3B8]">No attendance requests need review.</p> : requests.map((item) => (
              <div key={item.id} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid={`dashboard-attendance-request-${item.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-[#0A2540]">{item.staff_name}</p>
                    <p className="text-xs font-medium text-amber-800">{REQUEST_LABELS[item.request_type] || item.request_type?.replaceAll('_', ' ')} · {item.start_date}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => reviewRequest(item.id, 'approved')} disabled={reviewingId === item.id} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`dashboard-attendance-approve-${item.id}`}><Check className="h-4 w-4" /> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => reviewRequest(item.id, 'rejected')} disabled={reviewingId === item.id} className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`dashboard-attendance-reject-${item.id}`}><X className="h-4 w-4" /> Reject</Button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-[#475569]">{item.reason}</p>
                {item.request_type === 'time_correction' && <p className="mt-1 text-xs font-semibold text-sky-700">Correct to {item.time_from} – {item.time_to} IST</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};